// apiErrors — turn a failed API call into something a person can act on.
//
// axios rejects with an Error whose message is "Request failed with status
// code 403". That string reached a brand-new user's screen on 2026-10-13
// ("I just set up my account and I am seeing Error: Request failed with status
// code 403. What should I do?"). Nothing user-facing should render
// err.message from an HTTP failure; render describeLoadError(err).message.

export type LoadErrorKind =
  | "county_not_in_plan" // free/scout viewer listing a county outside their chosen set
  | "forbidden" // any other 403
  | "unauthorized" // 401 that survived the interceptor's refresh-and-retry
  | "server" // 5xx
  | "network" // no response at all (offline, CORS, DNS)
  | "other";

export interface LoadError {
  kind: LoadErrorKind;
  /** Human copy, safe to render as-is. Never the raw axios message. */
  message: string;
  status?: number;
  /** county_not_in_plan only: the counties the server says this viewer may list. */
  chosenCounties?: string[];
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x !== "") : [];
}

/**
 * describeLoadError maps a rejected API call (axios-shaped or not) to a
 * LoadError. `what` names the thing that failed to load, for the generic copy.
 */
export function describeLoadError(err: unknown, what = "these properties"): LoadError {
  const e = err as { response?: { status?: number; data?: unknown } } | null | undefined;  const response = e?.response;
  const status = typeof response?.status === "number" ? response.status : undefined;
  const data = (response?.data && typeof response.data === "object" ? response.data : {}) as Record<string, unknown>;

  if (status === 403 && data.error === "county_not_in_plan") {
    return {
      kind: "county_not_in_plan",
      status,
      chosenCounties: asStringArray(data.chosenCounties),
      message: "This county isn't on your plan.",
    };
  }
  if (status === 403) {
    return {
      kind: "forbidden",
      status,
      message: `Your plan doesn't include access to ${what}.`,
    };
  }
  if (status === 401) {
    return {
      kind: "unauthorized",
      status,
      message: "Your session has expired. Sign out and back in to continue.",
    };
  }
  if (status !== undefined && status >= 500) {
    return {
      kind: "server",
      status,
      message: `We couldn't load ${what} — our server had a problem. Try again in a moment.`,
    };
  }
  const code = (e as { code?: unknown } | null | undefined)?.code;
  if (!response && (code === "ERR_NETWORK" || (e as { request?: unknown } | null | undefined)?.request !== undefined)) {
    return {
      kind: "network",
      message: `We couldn't reach Parcely to load ${what}. Check your connection and try again.`,
    };
  }
  return {
    kind: "other",
    status,
    message: `We couldn't load ${what}. Try again in a moment.`,
  };
}
